import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'rbr_token';

const DEFAULT_BADGES = [
  { id: 'first-flame', name: 'First Flame', description: 'Read your first reflection', icon: 'Flame', earned: false, category: 'reading' },
  { id: 'bookworm', name: 'Bookworm', description: 'Read 5 reflections', icon: 'BookOpen', earned: false, category: 'reading' },
  { id: 'scholar', name: 'Scholar', description: 'Read 15 reflections', icon: 'GraduationCap', earned: false, category: 'reading' },
  { id: 'zen-master', name: 'Zen Master', description: 'Read all meditations', icon: 'Brain', earned: false, category: 'meditation' },
  { id: 'dream-weaver', name: 'Dream Weaver', description: 'Read all sleep stories', icon: 'Moon', earned: false, category: 'sleep' },
  { id: 'breath-of-life', name: 'Breath of Life', description: 'Read all breathing exercises', icon: 'Wind', earned: false, category: 'anxiety' },
  { id: 'streak-3', name: 'Streak Starter', description: '3-day streak', icon: 'Zap', earned: false, category: 'streak' },
  { id: 'streak-7', name: 'On Fire', description: '7-day streak', icon: 'Flame', earned: false, category: 'streak' },
  { id: 'streak-30', name: 'Unstoppable', description: '30-day streak', icon: 'Trophy', earned: false, category: 'streak' },
  { id: 'compassionate', name: 'Compassionate', description: 'Like 5 posts', icon: 'Heart', earned: false, category: 'engagement' },
  { id: 'curator', name: 'Curator', description: 'Bookmark 5 posts', icon: 'Bookmark', earned: false, category: 'engagement' },
  { id: 'explorer', name: 'Explorer', description: 'Visit all categories', icon: 'Compass', earned: false, category: 'exploration' },
  { id: 'affirmed', name: 'Affirmed', description: 'Read all affirmations', icon: 'Sparkles', earned: false, category: 'affirmation' },
  { id: 'early-bird', name: 'Early Bird', description: 'Read before 8 AM', icon: 'Sun', earned: false, category: 'time' },
  { id: 'night-owl', name: 'Night Owl', description: 'Read after 10 PM', icon: 'MoonStar', earned: false, category: 'time' },
];

// Compute which badges are earned based on user profile
const computeBadges = (profile) => {
  if (!profile) return DEFAULT_BADGES;
  const readPosts = profile.readPosts || [];
  const likedPosts = profile.likedPosts || [];
  const bookmarkedPosts = profile.bookmarkedPosts || [];
  const visitedCategories = profile.visitedCategories || [];
  const current = profile.streak?.current || 0;
  const earnedIds = new Set(profile.badges || []);

  return DEFAULT_BADGES.map((badge) => {
    let earned = earnedIds.has(badge.id);
    if (!earned) {
      switch (badge.id) {
        case 'first-flame': earned = readPosts.length >= 1; break;
        case 'bookworm': earned = readPosts.length >= 5; break;
        case 'scholar': earned = readPosts.length >= 15; break;
        case 'compassionate': earned = likedPosts.length >= 5; break;
        case 'curator': earned = bookmarkedPosts.length >= 5; break;
        case 'explorer': earned = visitedCategories.length >= 4; break;
        case 'streak-3': earned = current >= 3; break;
        case 'streak-7': earned = current >= 7; break;
        case 'streak-30': earned = current >= 30; break;
        case 'early-bird': earned = new Date().getHours() < 8 && readPosts.length > 0; break;
        case 'night-owl': earned = new Date().getHours() >= 22 && readPosts.length > 0; break;
        default: break;
      }
    }
    return earned ? { ...badge, earned: true } : { ...badge, earned: false };
  });
};

const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch (e) { console.warn('localStorage read failed', e); return null; }
};
const setToken = (token) => {
  try { localStorage.setItem(TOKEN_KEY, token); } catch (e) { console.warn('localStorage write failed', e); }
};
const clearToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); } catch (e) { console.warn('localStorage remove failed', e); }
};

export const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const AuthProvider = ({ children }) => {
  const [profile, setProfile] = useState(null); // full server-side user profile
  const [isLoading, setIsLoading] = useState(true);

  const applyProfile = useCallback((p) => {
    setProfile(p || null);
  }, []);

  // Show toast for unseen notifications. Toasts are persistent and only marked
  // seen when the user clicks the action or dismiss button.
  const shownNotificationIds = useRef(new Set());

  const markOneSeen = useCallback(async (notifId) => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/${notifId}/seen`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        const p = await res.json();
        setProfile(p);
      }
    } catch (e) {
      console.warn('markOneSeen failed', e);
    }
  }, []);

  useEffect(() => {
    if (!profile) return;
    const unseen = (profile.notifications || []).filter(
      (n) => !n.seen && !shownNotificationIds.current.has(n.id)
    );
    if (unseen.length === 0) return;

    unseen.forEach((n) => {
      shownNotificationIds.current.add(n.id);
      const baseOpts = {
        id: n.id, // dedupe within sonner
        duration: Infinity, // stay until clicked
        closeButton: true,
        onDismiss: () => markOneSeen(n.id),
      };

      if (n.type === 'friend_accepted') {
        toast.success(`🎉 ${n.fromUserName} and you are now friends!`, {
          ...baseOpts,
          action: {
            label: 'View',
            onClick: () => {
              markOneSeen(n.id);
              window.location.href = '/profile';
            },
          },
        });
      } else if (n.type === 'friend_request') {
        toast(`👋 ${n.fromUserName} sent you a friend request`, {
          ...baseOpts,
          action: {
            label: 'View',
            onClick: () => {
              markOneSeen(n.id);
              window.location.href = '/profile';
            },
          },
        });
      } else if (n.type === 'recommendation') {
        toast(`💫 ${n.fromUserName} recommends "${n.postTitle}"`, {
          ...baseOpts,
          action: n.postId
            ? {
                label: 'Read',
                onClick: () => {
                  markOneSeen(n.id);
                  window.location.href = `/post/${n.postId}`;
                },
              }
            : undefined,
        });
      }
    });
    // No more auto mark-seen on render — only on user interaction.
  }, [profile, markOneSeen]);

  // Fetch current user on mount if token exists, then mark today's check-in
  useEffect(() => {
    const init = async () => {
      const token = getToken();
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: authHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          applyProfile(data);
          // Keep streak alive on any active visit
          fetch(`${API_URL}/api/user/daily-checkin`, {
            method: 'POST',
            headers: authHeaders(),
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((p) => p && applyProfile(p))
            .catch(() => {});
        } else {
          clearToken();
        }
      } catch (e) {
        console.error('Failed to fetch profile', e);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [applyProfile]);

  // Persist newly-computed badges back to server (optional, lightweight)
  useEffect(() => {
    if (!profile) return;
    const computed = computeBadges(profile);
    const earnedIds = computed.filter((b) => b.earned).map((b) => b.id);
    const serverBadges = profile.badges || [];
    const diff = earnedIds.filter((id) => !serverBadges.includes(id));
    if (diff.length > 0) {
      // Fire-and-forget to keep server in sync
      fetch(`${API_URL}/api/user/badges`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ badges: earnedIds }),
      }).then((r) => r.ok && r.json()).then((p) => p && setProfile(p)).catch(() => {});
    }
  }, [profile?.readPosts?.length, profile?.likedPosts?.length, profile?.bookmarkedPosts?.length, profile?.visitedCategories?.length, profile?.streak?.current]);

  const signup = async (name, email, password, avatar) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, avatar }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.detail || 'Signup failed' };
      }
      const data = await res.json();
      setToken(data.token);
      applyProfile(data.user);
      // Kick the streak to 1 on day-zero
      fetch(`${API_URL}/api/user/daily-checkin`, { method: 'POST', headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => p && applyProfile(p))
        .catch(() => {});
      return { success: true };
    } catch (e) {
      return { success: false, error: 'Network error' };
    }
  };

  const login = async (email, password) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.detail || 'Login failed' };
      }
      const data = await res.json();
      setToken(data.token);
      applyProfile(data.user);
      // Mark today's visit so the streak ticks even if the user only browses
      fetch(`${API_URL}/api/user/daily-checkin`, { method: 'POST', headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => p && applyProfile(p))
        .catch(() => {});
      return { success: true };
    } catch (e) {
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    clearToken();
    setProfile(null);
  };

  // Log the user in with a token/profile pair we already have (e.g. right
  // after a successful password reset). Same effect as calling login() but
  // without a network round-trip.
  const hydrateSession = useCallback((token, profileObj) => {
    setToken(token);
    applyProfile(profileObj);
  }, [applyProfile]);

  const updateProfile = async (updates) => {
    // Only name supported via dedicated endpoint for now
    if ('name' in updates) {
      try {
        const res = await fetch(`${API_URL}/api/user/name`, {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: updates.name }),
        });
        if (res.ok) {
          const data = await res.json();
          applyProfile(data);
        }
      } catch (e) { console.error(e); }
    }
  };

  const updateProfilePicture = async (base64) => {
    try {
      const res = await fetch(`${API_URL}/api/user/profile-picture`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ profilePicture: base64 }),
      });
      if (res.ok) {
        const data = await res.json();
        applyProfile(data);
        return true;
      }
    } catch (e) { console.error(e); }
    return false;
  };

  const markPostRead = async (postId) => {
    if (!profile) return;
    try {
      const res = await fetch(`${API_URL}/api/user/read-post`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ postId }),
      });
      if (res.ok) {
        const data = await res.json();
        applyProfile(data);
      }
    } catch (e) { console.error(e); }
  };

  const toggleLikePost = async (postId) => {
    if (!profile) return false;
    try {
      const res = await fetch(`${API_URL}/api/user/like-post`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ postId }),
      });
      if (res.ok) {
        const data = await res.json();
        applyProfile(data);
        return true;
      }
    } catch (e) { console.error(e); }
    return false;
  };

  const toggleBookmarkPost = async (postId) => {
    if (!profile) return false;
    try {
      const res = await fetch(`${API_URL}/api/user/bookmark-post`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ postId }),
      });
      if (res.ok) {
        const data = await res.json();
        applyProfile(data);
        return true;
      }
    } catch (e) { console.error(e); }
    return false;
  };

  const visitCategory = async (categoryId) => {
    if (!profile) return;
    try {
      const res = await fetch(`${API_URL}/api/user/visit-category`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryId }),
      });
      if (res.ok) {
        const data = await res.json();
        applyProfile(data);
      }
    } catch (e) { console.error(e); }
  };

  const refreshProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        applyProfile(data);
      }
    } catch (e) { console.error(e); }
  };

  const computedBadges = computeBadges(profile);
  const earnedBadgeCount = computedBadges.filter((b) => b.earned).length;

  // Expose a user shape compatible with previous code
  const user = profile
    ? {
        id: profile.id,
        name: profile.name,
        email: profile.email,
        avatar: profile.avatar,
        profilePicture: profile.profilePicture,
        joinDate: profile.joinDate || new Date().toISOString(),
        friends: profile.friends || [],
        friendRequests: profile.friendRequests || [],
        isAdmin: !!profile.isAdmin,
      }
    : null;

  const value = {
    user,
    profile,
    streak: profile?.streak || { current: 0, longest: 0, lastReadDate: null, history: [] },
    badges: computedBadges,
    readPosts: profile?.readPosts || [],
    likedPosts: profile?.likedPosts || [],
    bookmarkedPosts: profile?.bookmarkedPosts || [],
    visitedCategories: profile?.visitedCategories || [],
    isLoading,
    signup,
    login,
    logout,
    hydrateSession,
    updateProfile,
    updateProfilePicture,
    markPostRead,
    toggleLikePost,
    toggleBookmarkPost,
    visitCategory,
    refreshProfile,
    earnedBadgeCount,
    totalBadgeCount: DEFAULT_BADGES.length,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
